import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core'
import CancelIcon from '@material-ui/icons/Cancel'
import policymanagementApi from '../../../../api/policymanagement.api'
import createCancellationPdf from '../../../../api/insurance.api'
import createTree from './tree'
import { CustomErrorPopup } from '../../../PopUps/CustomErrorPopup'
import { Provider, useCancelPolicyContext } from './context'
import { getTreeNode } from './components'
import { PolicyManagementContextStore } from '../../../../context/PolicyManagementContext'
import { awaitWithLoading } from '../../../../services/utils'
import insuranceApi, { sendPolicyCancellationNoticeEmail } from '../../../../api/insurance.api'

const CancelPolicy = ({ insurance }) => {
  const [state, dispatch] = useCancelPolicyContext()
  const [nextValue, setNextValue] = useState('')
  const myPoliciesContext = React.useContext(PolicyManagementContextStore)

  const {
    dialogOpen,
    currentNode,
    cancelationDate,
    cancelationReason,
    cancelationAction,
    cancelationText,
    odometerValues,
    odometerImages,
    showValidationInfo,
  } = state

  const linkRef = (useRef < HTMLAnchorElement) | (null > null)
  const [popUpErrorState, setPopUpErrorState] = React.useState({
    title: '',
    body: '',
    showPopUp: false,
  })
  const cancelErrorPopup = () => setPopUpErrorState({ showPopUp: false, title: '', body: '' })
  const setCurrentNode = useCallback(node => dispatch({ type: 'SET_CURRENT_NODE', payload: node }), [dispatch])
  const openDialog = useCallback(() => dispatch({ type: 'SET_DIALOG_OPEN', payload: true }), [dispatch])
  const closeDialog = useCallback(() => dispatch({ type: 'SET_DIALOG_OPEN', payload: false }), [dispatch])
  // context async methods
  const cancelPolicyAction = useCallback(async () => {
    dispatch({ type: 'REQUEST_START' })
    try {
      const data = {
        insuranceId: insurance.insuranceId,
        cancelationDate,
        cancelationReason,
        cancelationAction,
        cancelationText,
        odometerImages,
        odometerValues,
      }
      const res = await policymanagementApi.cancelPolicy(data)
      dispatch({ type: 'REQUEST_SUCCESS' })
      await awaitWithLoading(myPoliciesContext.loadPolicies())
      return res
    } catch (error) {
      console.log(error)
      dispatch({ type: 'REQUEST_ERROR', payload: error })
    }
  }, [dispatch, cancelationDate, cancelationReason, cancelationAction, cancelationText, odometerValues, odometerImages])

  ///////////////////////
  // create the navigation tree, passing tree dependencies
  const Tree = useMemo(() => createTree(dispatch, cancelPolicyAction), [dispatch, cancelPolicyAction])

  const handleCancellationPdfOnClick = async () => {
    const response = await awaitWithLoading(insuranceApi.sendCancellationNoticeEmailAndGeneratePDF(insurance.insuranceId))

    if (response.status === 204) {
      setPopUpErrorState({
        title: 'Notice',
        body: 'This state supports email generation only and does not support automatic DNOC PDF file generation. Please check your email for the auto-filled email text. Thank you.',
        showPopUp: true,
      })
    } else if (!response.ok) {
      setPopUpErrorState({
        title: response.data.title,
        body: response.data.messages && Object.values(response.data.messages)[0],
        showPopUp: true,
      })
    } else {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(response.data)
      link.download = 'Cancellation PDF.pdf'
      link.download = `Policy_${formatPolicyNumber(insurance.policyNumber)}_Cancellation.pdf`
      link.click()
      link.remove()
    }
  }

  const formatPolicyNumber = policyNumber => {
    return `VMT${policyNumber.padStart(11, '0')}`
  }
  useEffect(() => {
    // reset when dialog is closed
    if (dialogOpen === false) {
      dispatch({ type: 'RESET' })
      setCurrentNode(Tree)
      dispatch({
        type: 'SET_INSURANCE_ID',
        payload: insurance.insuranceId,
      })
      dispatch({
        type: 'SET_POLICY_VEHICLES',
        payload: insurance.insuredVehicles,
      })
      dispatch({
        type: 'SET_POLICY_DATES',
        payload: {
          startDate: new Date(insurance.validFromUtc.getTime()).setDate(insurance.validFromUtc.getDate() - 2),
          endDate: insurance.validUntilUtc,
          perMileUntil: insurance.perMileUntil,
        },
      })
    }
  }, [dialogOpen])

  useEffect(() => {
    // reset next value when changing node
    setNextValue('')
  }, [currentNode])

  if (!currentNode) return null
  if (typeof currentNode === 'object' && typeof currentNode.then === 'function') return null

  const {
    display: { caption, maybeOptions, maybeComponent: Component, nextCaption },
    maybeChildren,
    maybeNext,
    maybeBack,
    maybeValidation,
    last,
  } = currentNode

  return (
    <>
      <div className="col">
        <Button
          variant="text"
          onClick={() => {
            setCurrentNode(Tree)
            openDialog()
          }}
          color="primary"
          style={{ border: '1px solid', borderRadius: '.5rem', padding: '.75rem', width: '9.68rem' }}
          className="no-text-transform margin-bottom-basic"
          endIcon={<CancelIcon />}
        >
          Cancel Policy
        </Button>
      </div>
      <Dialog onClose={closeDialog} fullWidth={true} open={dialogOpen} maxWidth="md">
        <form
          onSubmit={async e => {
            // we only use a form here for convinience,
            // to utilize submit either by hitting enter or clicking the button
            e.preventDefault()
            // if current node has a validation function, run it
            if (maybeValidation && typeof maybeValidation === 'function') {
              if (maybeValidation(state) === false) return
            }
            // if current node is the last one, perform cleanup
            if (last) {
              // send cancelation request
              setCurrentNode(Tree)
              closeDialog()
            }
            // navigate based on pre-defined option
            if (maybeOptions && maybeChildren && nextValue) {
              const next = maybeChildren[nextValue]
              setCurrentNode(typeof next === 'function' ? next(Tree) : next)
              // navigate based on current node's next function
            } else if (maybeNext) setCurrentNode(typeof maybeNext === 'function' ? await maybeNext(Tree) : maybeNext)
          }}
        >
          <div style={{ minHeight: '24rem' }}>
            <DialogTitle>{caption}</DialogTitle>
            <DialogContent>
              {/* render node options, if they exist */}
              {maybeOptions &&
                Object.keys(maybeOptions).map(key => {
                  return (
                    <div style={{ marginBottom: '1rem' }} key={key}>
                      <input
                        type="radio"
                        id={key}
                        name="options"
                        value={nextValue}
                        onChange={event => setNextValue(event.currentTarget.id)}
                      />
                      <label htmlFor={key}>{maybeOptions[key]}</label>
                    </div>
                  )
                })}
              {/* render node custom component, if exists  */}
              {Component && <Component />}
              <div style={{ color: 'red', visibility: showValidationInfo ? 'visible' : 'hidden' }}>
                Please fill out missing info before moving next
              </div>
            </DialogContent>
          </div>
          <DialogActions style={{ flex: 1, justifyContent: 'space-between' }}>
            <Button
              color="primary"
              style={{
                visibility: last === undefined ? 'visible' : 'hidden',
              }}
              onClick={closeDialog}
            >
              Cancel
            </Button>
            <div>
              {maybeBack && (
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={() => {
                    const backNode = maybeBack && maybeBack(Tree)
                    backNode && setCurrentNode(backNode)
                  }}
                  style={{ marginRight: '1rem' }}
                >
                  Back
                </Button>
              )}
              <Button disabled={state.status === 'busy'} variant="contained" color="primary" type="submit">
                {nextCaption || 'Next'}
              </Button>
            </div>
          </DialogActions>
        </form>
      </Dialog>
      <CustomErrorPopup
        title={popUpErrorState.title}
        body={popUpErrorState.body}
        open={popUpErrorState.showPopUp}
        onCancel={cancelErrorPopup}
      />
    </>
  )
}

export default props => (
  <Provider>
    <CancelPolicy {...props} />
  </Provider>
)
